import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useRuntimeHealthAccess } from "@/hooks/useRuntimeHealthAccess";
import { CheckCircle2, LifeBuoy, RefreshCw, Search, ShieldCheck, Siren } from "lucide-react";
import { applyDrilldownFilters, type DrilldownFilters } from "@/lib/runtimeHealthBus";

type Incident = {
  id: string;
  created_at: string;
  title: string;
  status: "open" | "acknowledged" | "resolved";
  severity: "info" | "warn" | "error";
  source: "manual" | "alert" | "regression";
  route: string | null;
  fingerprint: string | null;
  release: string | null;
  drilldown: DrilldownFilters | null;
  event_count: number | null;
  notes: string | null;
  acknowledged_at: string | null;
  resolved_at: string | null;
  escalation_level: number | null;
  last_escalated_at: string | null;
  slack_thread_ts: string | null;
};

type Escalation = {
  id: string;
  incident_id: string;
  level: number;
  channel: string;
  destination: string | null;
  unacknowledged_minutes: number | null;
  outcome: "delivered" | "failed" | "skipped";
  error: string | null;
  created_at: string;
};

const STATUS_FILTERS = ["all", "open", "acknowledged", "resolved"] as const;

export function FrontendErrorIncidentsPanel({ prefill }: { prefill?: DrilldownFilters }) {
  const { toast } = useToast();
  const { canTriage, canConfigure } = useRuntimeHealthAccess();
  const [rows, setRows] = useState<Incident[]>([]);
  const [escalations, setEscalations] = useState<Record<string, Escalation[]>>({});
  const [status, setStatus] = useState<(typeof STATUS_FILTERS)[number]>("open");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState<Incident["severity"]>("warn");


  const load = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from("frontend_error_incidents")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100);
    if (status !== "all") query = query.eq("status", status);
    const [{ data, error }, { data: esc }] = await Promise.all([
      query,
      supabase
        .from("frontend_error_incident_escalations")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(300),
    ]);
    if (error) {
      toast({ title: "Could not load incidents", description: error.message, variant: "destructive" });
    } else {
      setRows((data ?? []) as unknown as Incident[]);
    }
    const grouped: Record<string, Escalation[]> = {};
    for (const row of (esc ?? []) as unknown as Escalation[]) {
      (grouped[row.incident_id] ??= []).push(row);
    }
    setEscalations(grouped);
    setLoading(false);
  }, [status, toast]);

  useEffect(() => {
    void load();
  }, [load]);


  const counts = useMemo(
    () => ({
      open: rows.filter((r) => r.status === "open").length,
      acknowledged: rows.filter((r) => r.status === "acknowledged").length,
    }),
    [rows],
  );

  const create = async () => {
    if (!title.trim()) {
      toast({ title: "Give the incident a title", variant: "destructive" });
      return;
    }
    const { data: session } = await supabase.auth.getSession();
    const { error } = await supabase.from("frontend_error_incidents").insert({
      title: title.trim(),
      severity,
      source: "manual",
      route: prefill?.route || null,
      fingerprint: prefill?.fingerprint || null,
      release: prefill?.release || null,
      drilldown: (prefill ?? {}) as never,
      created_by: session.session?.user.id ?? null,
    });
    if (error) {
      toast({ title: "Could not create incident", description: error.message, variant: "destructive" });
      return;
    }
    setTitle("");
    toast({ title: "Incident opened" });
    void load();
  };

  const transition = async (incident: Incident, next: Incident["status"]) => {
    setBusyId(incident.id);
    const { data: session } = await supabase.auth.getSession();
    const uid = session.session?.user.id ?? null;
    const changes =
      next === "acknowledged"
        ? { status: next, acknowledged_by: uid, acknowledged_at: new Date().toISOString() }
        : next === "resolved"
          ? { status: next, resolved_by: uid, resolved_at: new Date().toISOString() }
          : { status: next, acknowledged_at: null, resolved_at: null, resolved_by: null };
    const { error } = await supabase
      .from("frontend_error_incidents")
      .update(changes as never)
      .eq("id", incident.id);
    setBusyId(null);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return;
    }
    void load();
  };

  const saveNotes = async (incident: Incident, notes: string) => {
    if ((incident.notes ?? "") === notes) return;
    const { error } = await supabase
      .from("frontend_error_incidents")
      .update({ notes: notes || null })
      .eq("id", incident.id);
    if (error) toast({ title: "Could not save notes", description: error.message, variant: "destructive" });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <LifeBuoy className="size-4 text-primary" /> Incident triage
          </CardTitle>
          <CardDescription>
            Alerts and release regressions open incidents automatically. Acknowledge one to claim it,
            resolve it when the fix ships, and jump straight to the matching drilldown query.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger className="w-40" aria-label="Filter incidents by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s === "all" ? "All statuses" : s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!canTriage && (
          <p className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            You have read-only access to incidents. Moderator or admin role is required to
            acknowledge, resolve, or annotate them.
          </p>
        )}

        {canConfigure && (
          <div className="flex flex-wrap items-end gap-3 rounded-md border border-border p-3">
            <div className="min-w-[16rem] flex-1 space-y-1.5">
              <Label htmlFor="inc-title" className="text-xs">
                New incident {prefill?.route || prefill?.fingerprint ? "(uses the current drilldown filters)" : ""}
              </Label>
              <Input
                id="inc-title"
                value={title}
                placeholder="Dashboard crash after 1.4.2"
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inc-sev" className="text-xs">Severity</Label>
              <Select value={severity} onValueChange={(v) => setSeverity(v as Incident["severity"])}>
                <SelectTrigger id="inc-sev" className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warn">Warn</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => void create()}>Open incident</Button>
          </div>
        )}

        <div className="flex gap-3 text-xs text-muted-foreground">
          <span>{counts.open} open</span>
          <span>{counts.acknowledged} acknowledged</span>
        </div>

        {rows.length === 0 && !loading && (
          <p className="text-sm text-muted-foreground">No incidents with this status.</p>
        )}

        {rows.map((incident) => (
          <div key={incident.id} className="space-y-2 rounded-md border border-border p-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant={incident.severity === "error" ? "destructive" : "secondary"}>
                {incident.severity}
              </Badge>
              <Badge variant="outline">{incident.status}</Badge>
              <span>{incident.source}</span>
              <span>{new Date(incident.created_at).toLocaleString()}</span>
              {incident.route && <span className="font-mono">{incident.route}</span>}
              {incident.release && <span className="font-mono">rel:{incident.release}</span>}
              {incident.fingerprint && <span className="font-mono">fp:{incident.fingerprint}</span>}
              {incident.event_count != null && <span>{incident.event_count} events</span>}
              {(incident.escalation_level ?? 0) > 0 && (
                <Badge variant="destructive" className="gap-1">
                  <Siren className="size-3" /> escalated L{incident.escalation_level}
                </Badge>
              )}
            </div>
            <p className="text-sm font-medium">{incident.title}</p>

            <div className="flex flex-wrap gap-2">
              {canTriage && incident.status !== "acknowledged" && incident.status !== "resolved" && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === incident.id}
                  onClick={() => void transition(incident, "acknowledged")}
                >
                  <ShieldCheck className="size-3.5" /> Acknowledge
                </Button>
              )}
              {canTriage && incident.status !== "resolved" && (
                <Button
                  size="sm"
                  disabled={busyId === incident.id}
                  onClick={() => void transition(incident, "resolved")}
                >
                  <CheckCircle2 className="size-3.5" /> Resolve
                </Button>
              )}
              {canTriage && incident.status === "resolved" && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === incident.id}
                  onClick={() => void transition(incident, "open")}
                >
                  Reopen
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  applyDrilldownFilters({
                    route: incident.route ?? undefined,
                    fingerprint: incident.fingerprint ?? undefined,
                    release: incident.release ?? undefined,
                    ...(incident.drilldown ?? {}),
                  })
                }
              >
                <Search className="size-3.5" /> Open in drilldown
              </Button>
            </div>

            {(incident.acknowledged_at || incident.resolved_at) && (
              <p className="text-xs text-muted-foreground">
                {incident.acknowledged_at && `Acknowledged ${new Date(incident.acknowledged_at).toLocaleString()}. `}
                {incident.resolved_at && `Resolved ${new Date(incident.resolved_at).toLocaleString()}.`}
              </p>
            )}

            {(escalations[incident.id]?.length ?? 0) > 0 && (
              <div className="space-y-1 rounded-md bg-muted/40 p-2">
                <p className="text-xs font-medium">Escalations &amp; follow-ups</p>
                {escalations[incident.id].map((e) => (
                  <p key={e.id} className="text-xs text-muted-foreground">
                    L{e.level} · {e.channel}
                    {e.destination ? ` → ${e.destination.slice(0, 48)}` : ""} ·{" "}
                    {e.unacknowledged_minutes != null ? `${e.unacknowledged_minutes}m unacked · ` : ""}
                    <span className={e.outcome === "failed" ? "text-destructive" : ""}>{e.outcome}</span>
                    {e.error ? ` — ${e.error}` : ""} · {new Date(e.created_at).toLocaleString()}
                  </p>
                ))}
                {incident.slack_thread_ts && (
                  <p className="font-mono text-xs text-muted-foreground">
                    Slack thread {incident.slack_thread_ts}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor={`inc-notes-${incident.id}`} className="text-xs">Triage notes</Label>
              <Textarea
                id={`inc-notes-${incident.id}`}
                defaultValue={incident.notes ?? ""}
                rows={2}
                disabled={!canTriage}
                placeholder="What we know, what we tried, what shipped."
                onBlur={(e) => void saveNotes(incident, e.target.value.trim())}
              />
            </div>
          </div>
        ))}

      </CardContent>
    </Card>
  );
}

export default FrontendErrorIncidentsPanel;
